import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Grid,
  Header,
  Segment,
  Icon,
  Button,
} from 'semantic-ui-react';
import { HeaderAndPage } from '../../common/components';
import { AuthStdFooter } from '../../utils/auth-std-footer';
import ProcessGuide from '../../components/help/ProcessGuide';
import {
  fetchPrototypes,
  selectActivePrototype,
  clearActivePrototype,
  setWizardStep,
} from '../../slices/litGroupPrototypeSlice';
import PrototypeList from '../../components/prototype/PrototypeList';
import PrototypeWizard from '../../components/prototype/PrototypeWizard';
import PrototypeDetailsForm from '../../components/prototype/PrototypeDetailsForm';
import '../../styles/landing-pages.css';

// Import assets
import myLibrary from '../../assets/myLibrary.svg';

/**
 * TextLibraryLandingPage — Literary Group Builder
 *
 * Route: /add-litgroups-books (was: /landing/text-library)
 * Also rendered inside the Dashboard shell via menuId TEXT-LIBRARY.
 *
 * Layout:
 *   - Left panel (4 cols): PrototypeList — card list of user's literary groups
 *   - Right panel (12 cols): PrototypeWizard (if one is selected) or
 *     PrototypeDetailsForm in create mode (if "New Literary Group" clicked)
 *
 * 2026-04-21 (PROTOTYPE_UX_UPGRADE_SPEC §11 Phase A):
 *  - Removed the absolutely-positioned "← Dashboard" button and handed it to
 *    HeaderAndPage via its `toolbarComponent` slot; no more magic pixel offsets.
 *  - Deleted the dead commented-out legacy header Grid block.
 *  - Removed redundant inner margin wrappers (HeaderAndPage now owns spacing).
 */
const TextLibraryLandingPage = () => {
  const dispatch = useDispatch();
  const activePrototype = useSelector(selectActivePrototype);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch prototypes on mount
  useEffect(() => {
    dispatch(fetchPrototypes());
  }, [dispatch]);

  const handleNewPrototype = () => {
    dispatch(clearActivePrototype());
    setIsCreating(true);
  };

  const handlePrototypeCreated = (result) => {
    // U2 (2026-04-21): auto-advance to Step 2 (Add Books) after successful create.
    //
    // The 600ms delay keeps the `PrototypeDetailsForm` mounted long enough for its
    // green "Literary Group 'X' created!" success flash to actually render — previously
    // the parent flipped `isCreating → false` the instant onCreated fired, unmounting
    // the form before React painted the success message. That latent bug is fixed here.
    //
    // After creation, the slice auto-sets the new prototype as activePrototype, so
    // toggling isCreating → false renders <PrototypeWizard /> on Step 2.
    //
    // See PROTOTYPE_UX_UPGRADE_SPEC §2.
    setTimeout(() => {
      setIsCreating(false);
      dispatch(setWizardStep(2));
    }, 600);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  // When user selects a prototype from the list, exit create mode
  useEffect(() => {
    if (activePrototype) {
      setIsCreating(false);
    }
  }, [activePrototype]);

  // Determine what to show in the right panel
  const renderRightPanel = () => {
    if (isCreating) {
      return (
        <PrototypeDetailsForm
          mode="create"
          onCreated={handlePrototypeCreated}
          onCancel={handleCancelCreate}
        />
      );
    }

    // Show wizard for selected prototype, or empty state.
    //
    // KEYED ON THE PROTOTYPE so switching Literary Groups gives a FRESH wizard.
    //
    // Without the key React keeps one instance across selections, and the wizard's local
    // state comes with it — `editingLiveBook`, `addingToSeries`, `editingBook`, `castOpen`.
    // The Books step renders `editingLiveBook ? <EditLiveBookForm> : …`, so a book opened
    // for editing in one group stayed open when you picked a different group and clicked
    // Books: you saw the previous group's book form instead of this group's list, and every
    // later selection showed that same stale form. Redux was innocent — activeLiveBooks was
    // being replaced correctly the whole time; only the component's own state was stranded.
    //
    // A key rather than a reset effect: an effect has to name every piece of state it
    // clears, and the next one added silently escapes it.
    return <PrototypeWizard key={activePrototype?.uuid || 'none'} />;
  };

  // "← Dashboard" shortcut — rendered in HeaderAndPage's toolbar slot so it
  // flows with the layout instead of being absolutely positioned over it.
  const dashboardShortcut = (
    <div style={{ textAlign: 'right' }}>
      <Button
        as={Link}
        to="/dashboard"
        size="small"
        className="landing-goto-button"
      >
        <Icon name="arrow left" /> Dashboard
      </Button>
    </div>
  );

  return (
    <div className="landing-page text-library-landing" style={{ marginTop: "-50px" }}>
      {/* Mounted by the PAGE, not by Layout — this page is normally reached as a dashboard
          sub-page, where the URL stays /dashBoard and any route-matching misses it entirely. */}
      <ProcessGuide guideKey="litgroup-builder" />
      <HeaderAndPage
        doCenter={true}
        headerImage={myLibrary}
        headerText="Literary Group Builder"
        subHeaderText={
          <p
            className="landing-subheading-text"
            style={{ maxWidth: '600px', margin: '0 auto' }}
          >
            Create LitGroup details, upload book PDFs, covers, and author images.
            Then publish when ready, or automatically.
          </p>
        }
        toolbarComponent={dashboardShortcut}
      >
        <Segment
          basic
          className="articleListWidth"
          style={{ marginBottom: '70px', minWidth: '1024px', paddingTop: '0px' }}
        >
          <Grid>
            <Grid.Row>
              {/* Left Panel: Literary Group list.
                  20px wider than the 4/16 the grid gives it, taken from the right panel so the
                  row still adds to 100%. Inline rather than a new width class because Semantic's
                  columns are whole sixteenths — there is no "4 columns and a bit", and the next
                  step up (5/16) is a 60px jump that costs the wizard more than the list gains. */}
              <Grid.Column width={4} style={{ width: 'calc(25% + 20px)' }}>
                <Header as="h3" style={{ marginBottom: '0.5em' }}>
                  <Icon name="list" />
                  <Header.Content>Literary Groups</Header.Content>
                </Header>
                <PrototypeList onNewPrototype={handleNewPrototype} />
              </Grid.Column>

              {/* Right Panel: Wizard or Create Form */}
              <Grid.Column width={12} style={{ width: 'calc(75% - 20px)' }}>{renderRightPanel()}</Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
        {AuthStdFooter()}
      </HeaderAndPage>
    </div>
  );
};

export default TextLibraryLandingPage;
